/*
  Cocoon native page behaviours: accordions, tabs, sliders.
  Replaces the PageFly runtime. Markup contract (see cocoon-native.css):

  Accordion
    <div class="cc-acc" data-cc-acc [data-multiple]>
      <div class="cc-acc-item [is-open]">
        <button class="cc-acc-head" type="button" aria-expanded="false">…</button>
        <div class="cc-acc-panel" hidden><div class="cc-acc-body">…</div></div>
      </div>
    </div>

  Tabs
    <div class="cc-tabs" data-cc-tabs>
      <div class="cc-tabs__menu" role="tablist"><button class="cc-tab" role="tab">…</button>…</div>
      <div class="cc-tabs__content"><div class="cc-tabs__panel" role="tabpanel">…</div>…</div>
    </div>

  Slider
    <div class="cc-slider" data-cc-slider [data-loop] [data-autoplay="3000"] style="--per-xs:1;--per-md:3;--gap:15px">
      <div class="cc-slider__track"><div class="cc-slide">…</div>…</div>
      (arrows / dots are added by this script unless data-no-arrows / data-no-dots)
    </div>
*/
(function () {
  'use strict';

  // Run `done` once, when the height transition ends or after 500ms, whichever comes first
  function finishAfterTransition(panel, done) {
    var called = false;
    function finish() {
      if (called) return;
      called = true;
      panel.removeEventListener('transitionend', finish);
      done();
    }
    panel.addEventListener('transitionend', finish);
    setTimeout(finish, 500);
  }

  function initAccordion(root) {
    if (root.__ccInit) return;
    root.__ccInit = true;
    var multiple = root.hasAttribute('data-multiple');
    var items = Array.prototype.slice.call(root.querySelectorAll(':scope > .cc-acc-item'));

    function setOpen(item, open, animate) {
      var head = item.querySelector(':scope > .cc-acc-head');
      var panel = item.querySelector(':scope > .cc-acc-panel');
      if (!head || !panel) return;
      item.classList.toggle('is-open', open);
      head.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        panel.hidden = !open;
        panel.style.height = '';
        return;
      }
      if (open) {
        panel.hidden = false;
        var h = panel.scrollHeight;
        panel.style.height = '0px';
        void panel.offsetHeight; // force reflow so the transition starts from 0
        panel.style.height = h + 'px';
        finishAfterTransition(panel, function () {
          panel.style.height = '';
        });
      } else {
        panel.style.height = panel.scrollHeight + 'px';
        void panel.offsetHeight; // force reflow so the transition starts from the full height
        panel.style.height = '0px';
        finishAfterTransition(panel, function () {
          panel.hidden = true;
          panel.style.height = '';
        });
      }
    }

    items.forEach(function (item) {
      setOpen(item, item.classList.contains('is-open'), false);
      var head = item.querySelector(':scope > .cc-acc-head');
      if (!head) return;
      head.addEventListener('click', function () {
        var open = !item.classList.contains('is-open');
        if (open && !multiple) {
          items.forEach(function (other) {
            if (other !== item && other.classList.contains('is-open')) setOpen(other, false, true);
          });
        }
        setOpen(item, open, true);
      });
    });
  }

  function initTabs(root) {
    if (root.__ccInit) return;
    root.__ccInit = true;
    var tabs = Array.prototype.slice.call(root.querySelectorAll(':scope > .cc-tabs__menu > .cc-tab'));
    var panels = Array.prototype.slice.call(root.querySelectorAll(':scope > .cc-tabs__content > .cc-tabs__panel'));

    function select(index) {
      tabs.forEach(function (tab, i) {
        var active = i === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      });
      panels.forEach(function (panel, i) {
        panel.hidden = i !== index;
      });
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        select(i);
      });
      tab.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % tabs.length;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + tabs.length) % tabs.length;
        if (next !== null) {
          e.preventDefault();
          select(next);
          tabs[next].focus();
        }
      });
    });
    var initial = tabs.findIndex(function (t) {
      return t.classList.contains('is-active');
    });
    select(initial < 0 ? 0 : initial);
  }

  function initSlider(root) {
    if (root.__ccInit) return;
    root.__ccInit = true;
    var track = root.querySelector(':scope > .cc-slider__track');
    if (!track) return;
    var slides = Array.prototype.slice.call(track.children);
    if (!slides.length) return;
    var loop = root.hasAttribute('data-loop');
    var autoplay = parseInt(root.getAttribute('data-autoplay') || '0', 10);

    function perView() {
      var w = slides[0].getBoundingClientRect().width;
      return w ? Math.max(1, Math.round(track.clientWidth / w)) : 1;
    }
    function pageCount() {
      return Math.max(1, slides.length - perView() + 1);
    }
    function current() {
      var w = slides[0].getBoundingClientRect().width || 1;
      return Math.round(track.scrollLeft / w);
    }
    function go(index) {
      var count = pageCount();
      if (loop) index = (index + count) % count;
      index = Math.max(0, Math.min(index, count - 1));
      track.scrollTo({ left: slides[index].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
    }

    var prev, next, dots;
    if (!root.hasAttribute('data-no-arrows')) {
      prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'cc-slider__nav cc-slider__nav--prev';
      prev.setAttribute('aria-label', 'Previous');
      next = document.createElement('button');
      next.type = 'button';
      next.className = 'cc-slider__nav cc-slider__nav--next';
      next.setAttribute('aria-label', 'Next');
      prev.addEventListener('click', function () {
        go(current() - 1);
      });
      next.addEventListener('click', function () {
        go(current() + 1);
      });
      root.appendChild(prev);
      root.appendChild(next);
    }
    if (!root.hasAttribute('data-no-dots')) {
      dots = document.createElement('div');
      dots.className = 'cc-slider__dots';
      root.appendChild(dots);
    }

    function render() {
      var count = pageCount();
      var index = current();
      if (dots) {
        if (dots.children.length !== count) {
          dots.innerHTML = '';
          for (var i = 0; i < count; i++) {
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'cc-slider__dot';
            dot.setAttribute('aria-label', 'Slide ' + (i + 1));
            dot.addEventListener('click', go.bind(null, i));
            dots.appendChild(dot);
          }
        }
        Array.prototype.forEach.call(dots.children, function (d, i) {
          d.classList.toggle('is-active', i === index);
        });
        dots.hidden = count < 2;
      }
      if (prev && next) {
        prev.disabled = !loop && index <= 0;
        next.disabled = !loop && index >= count - 1;
        prev.hidden = next.hidden = count < 2;
      }
    }

    var t;
    track.addEventListener('scroll', function () {
      clearTimeout(t);
      t = setTimeout(render, 60);
    });
    window.addEventListener('resize', render);
    render();

    if (autoplay > 0) {
      var timer = setInterval(function () {
        if (!document.hidden) go(current() + 1);
      }, autoplay);
      root.addEventListener('pointerdown', function () {
        clearInterval(timer);
      });
    }
  }

  function init(scope) {
    scope = scope || document;
    scope.querySelectorAll('[data-cc-acc]').forEach(initAccordion);
    scope.querySelectorAll('[data-cc-tabs]').forEach(initTabs);
    scope.querySelectorAll('[data-cc-slider]').forEach(initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }
  // Theme editor: re-init a section after it is re-rendered
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  window.CocoonNative = { init: init };
})();
